import { makeAutoObservable, runInAction } from 'mobx';
import { IFedexV2MfaOptions } from '../../../../../../../models/IntegrationsTypes';
import { FedexV2CredentialsPayload } from './fedex-v2-credentials.types';
import FedexV2AddressStore from './fedex-v2-address-store';
import FedexV2EulaStore from './fedex-v2-eula-store';
import FedexV2MfaStore from './fedex-v2-mfa-store';
import FedexV2RegistrationApi from './fedex-v2-registration-api';
import FedexV2RegistrationWizardStore from './fedex-v2-registration-wizard-store';

type RegistrationOutcomeHandlers = {
    onSuccess: (payload: FedexV2CredentialsPayload) => void;
    onCancel: () => void;
};

class FedexV2RegistrationStore {
    private readonly _address = new FedexV2AddressStore();
    private readonly _eula = new FedexV2EulaStore();
    private readonly _mfa = new FedexV2MfaStore();
    private readonly _wizard = new FedexV2RegistrationWizardStore();
    private readonly _api: FedexV2RegistrationApi;
    private readonly _handlers: RegistrationOutcomeHandlers;

    constructor(isSandbox: boolean, handlers: RegistrationOutcomeHandlers) {
        this._api = new FedexV2RegistrationApi(isSandbox);
        this._handlers = handlers;
        makeAutoObservable(this, {}, { autoBind: true });
    }

    get address() {
        return this._address;
    }

    get eula() {
        return this._eula;
    }

    get mfa() {
        return this._mfa;
    }

    get step() {
        return this._wizard.step;
    }

    get isLoading() {
        return this._wizard.isLoading;
    }

    get error() {
        return this._wizard.error;
    }

    get isComplete() {
        return this._wizard.isComplete;
    }

    get mfaSelectOptions(): IFedexV2MfaOptions {
        const options = this._mfa.mfaOptions;
        if (!options) {
            throw new Error('FedEx MFA options are not available');
        }
        return options;
    }

    abandon() {
        this._wizard.abandon();
    }

    close() {
        if (this._wizard.isClosed) {
            return;
        }

        if (this._wizard.isComplete) {
            this._wizard.markClosed();
            this._handlers.onSuccess(this.buildPayload());
            return;
        }

        this._wizard.abandon();
        this._handlers.onCancel();
    }

    acceptEula() {
        if (!this._eula.canAccept) {
            return;
        }

        this._wizard.goToAddress();
    }

    submitAddress() {
        return this._wizard.runAsync(async () => {
            const result = await this._api.validateAddress({
                accountNumber: this._address.accountNumber,
                customerName: this._address.customerName,
                address: this._address.buildAddress(),
            });

            runInAction(() => {
                if (this._wizard.isClosed) {
                    return;
                }

                if (result.type === 'credentials') {
                    this._wizard.complete(result.credentials);
                    return;
                }

                this._mfa.applyMfaOptions(result.mfaOption);
                this._wizard.goToMfaSelect();
            });
        });
    }

    startPinVerification() {
        return this._wizard.runAsync(async () => {
            await this._api.generatePin(
                this._mfa.accountAuthToken,
                this._mfa.pinDeliveryOption,
            );

            runInAction(() => {
                if (this._wizard.isClosed) {
                    return;
                }

                this._wizard.goToMfaInput();
            });
        });
    }

    submitMfa() {
        return this._wizard.runAsync(async () => {
            const credentials = await this._api.validatePin(
                this._mfa.accountAuthToken,
                this._mfa.secureCodePin,
            );

            runInAction(() => {
                if (this._wizard.isClosed) {
                    return;
                }

                this._wizard.complete(credentials);
            });
        });
    }

    private buildPayload(): FedexV2CredentialsPayload {
        const childCredentials = this._wizard.childCredentials;
        if (!childCredentials) {
            throw new Error('FedEx child credentials are not available');
        }

        return {
            api_key: this._address.accountNumber,
            child_key: childCredentials.childKey,
            child_secret: childCredentials.childSecret,
        };
    }
}

export default FedexV2RegistrationStore;
